import React, { useState } from 'react';
import { Monitor, Smartphone } from 'lucide-react';
import Input from '../../../components/ui/Input';

export default function TemplatePreview({ subject, html, variables, onVariableChange }) {
  const [previewMode, setPreviewMode] = useState('desktop');

  const renderPreview = () => {
    let renderedHtml = html;
    let renderedSubject = subject;

    Object.keys(variables || {})?.forEach((key) => {
      const value = variables?.[key] || `{{${key}}}`;
      const regex = new RegExp(`{{${key}}}`, 'g');
      renderedHtml = renderedHtml?.replace(regex, value);
      renderedSubject = renderedSubject?.replace(regex, value);
    });

    return { renderedHtml, renderedSubject };
  };

  const { renderedHtml, renderedSubject } = renderPreview();

  const extractVariables = () => {
    const matches = html?.match(/{{(\w+)}}/g);
    if (!matches) return [];
    return [...new Set(matches.map(m => m.replace(/[{}]/g, '')))];
  };

  const templateVariables = extractVariables();

  return (
    <div className="max-w-4xl mx-auto">
      {/* Preview Mode Toggle */}
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-lg font-semibold text-gray-900">Preview</h2>
        <div className="flex gap-2">
          <button
            onClick={() => setPreviewMode('desktop')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
              previewMode === 'desktop' ?'bg-indigo-600 text-white' :'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
            }`}
          >
            <Monitor className="w-4 h-4" />
            Desktop
          </button>
          <button
            onClick={() => setPreviewMode('mobile')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
              previewMode === 'mobile' ?'bg-indigo-600 text-white' :'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
            }`}
          >
            <Smartphone className="w-4 h-4" />
            Mobile
          </button>
        </div>
      </div>
      {/* Variable Values Editor */}
      {templateVariables?.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 p-4 mb-6">
          <h3 className="text-sm font-semibold text-gray-900 mb-3">
            Preview Variables
          </h3>
          <div className="grid grid-cols-2 gap-4">
            {templateVariables?.map((variable) => (
              <Input
                key={variable}
                label={variable?.replace(/_/g, ' ')}
                value={variables?.[variable] || ''}
                onChange={(e) =>
                  onVariableChange({ ...variables, [variable]: e?.target?.value })
                }
                placeholder={`{{${variable}}}`}
              />
            ))}
          </div>
        </div>
      )}
      {/* Email Preview */}
      <div
        className={`bg-white rounded-lg shadow-lg transition-all ${
          previewMode === 'desktop' ? 'max-w-4xl' : 'max-w-md mx-auto'
        }`}
      >
        {/* Email Header */}
        <div className="border-b border-gray-200 p-4">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-full bg-indigo-600 flex items-center justify-center text-white font-semibold">
              C
            </div>
            <div className="flex-1">
              <div className="text-sm font-semibold text-gray-900">
                Your Company Name
              </div>
              <div className="text-xs text-gray-500">
                noreply@yourcompany.com
              </div>
            </div>
          </div>
          <div className="text-lg font-semibold text-gray-900">
            {renderedSubject || 'No subject'}
          </div>
        </div>

        {/* Email Body */}
        <div className="p-6">
          {renderedHtml ? (
            <div
              dangerouslySetInnerHTML={{ __html: renderedHtml }}
              className="prose prose-sm max-w-none"
            />
          ) : (
            <div className="text-center text-gray-400 py-12">
              <p>No content to preview</p>
              <p className="text-sm mt-2">
                Add blocks to the editor to see the preview
              </p>
            </div>
          )}
        </div>

        {/* Email Footer */}
        <div className="border-t border-gray-200 p-4 text-xs text-gray-500 text-center">
          <p>This is a preview of your email template</p>
          <p className="mt-1">
            © 2025 Your Company. All rights reserved.
          </p>
        </div>
      </div>
      <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
        <p className="text-sm text-yellow-800">
          <strong>Note:</strong> This is a preview with sample variable values.
          Actual emails will use real data from your system.
        </p>
      </div>
    </div>
  );
}